import { createSelector } from 'reselect';

const selectAppointmentDraft = (state) => state.appointment.draft;

const selectShop = createSelector(
  selectAppointmentDraft,
  (appointmentDraft) => appointmentDraft.shop || {},
);

const selectRelatedAppointmentDrafts = createSelector(
  selectAppointmentDraft,
  (appointmentDraft) => appointmentDraft.addons,
);

const selectAppointmentType = createSelector(
  selectAppointmentDraft,
  (appointmentDraft) => appointmentDraft.appointmentType,
);

const selectRelatedAppointmentDraftById = (appointmentTypeId) =>
  createSelector(selectRelatedAppointmentDrafts, (addons) => {
    // currently we assume can have only one related appointment type,
    // so if appointmentTypeId was not passed then return the first appointment type
    if (!appointmentTypeId && addons.length === 1) {
      return addons[0];
    }

    return addons.find(
      ({ appointmentType = {} }) =>
        appointmentType.id === appointmentTypeId || appointmentType.localId === appointmentTypeId,
    );
  });

const selectRelatedAppointmentTypeById = (appointmentTypeId) =>
  createSelector(
    selectRelatedAppointmentDraftById(appointmentTypeId),
    ({ appointmentType = {} } = {}) => appointmentType,
  );

const selectAppointmentDraftById = (appointmentTypeId) =>
  createSelector(
    selectAppointmentDraft,
    selectRelatedAppointmentDraftById(appointmentTypeId),
    ({ addons: ignored, ...appointmentDraft }, addon) => {
      const appointmentDrafts = [appointmentDraft, ...(addon ? [addon] : [])];

      return (
        appointmentDrafts.find(
          (draft) =>
            draft.appointmentType.id === appointmentTypeId ||
            draft.appointmentType.localId === appointmentTypeId,
        ) || {}
      );
    },
  );

const selectAppointmentTypeId = createSelector(
  selectAppointmentType,
  (appointmentType) => appointmentType.id,
);

const selectAppointmentTypePrice = createSelector(
  selectAppointmentType,
  ({ price = '' } = {}) => price,
);

const selectAppointmentTypeName = createSelector(
  selectAppointmentType,
  ({ name = '' } = {}) => name,
);

const selectRelatedAppointmentTypeName = (relatedAppointmentTypeId) =>
  createSelector(
    selectRelatedAppointmentTypeById(relatedAppointmentTypeId),
    (relatedAppointmentType) => relatedAppointmentType.name,
  );

const selectRelatedAppointmentTypes = createSelector(selectAppointmentDraft, (appointmentDraft) =>
  appointmentDraft.addons.map((addon) => addon.appointmentType),
);

const selectRelatedAppointmentTypesIds = createSelector(
  selectRelatedAppointmentTypes,
  (appointmentTypes) =>
    appointmentTypes.map((appointmentType) => appointmentType.id).filter(Boolean),
);

const selectAllAppointmentTypes = createSelector(
  selectAppointmentType,
  selectRelatedAppointmentTypes,
  (appointmentType, relatedAppointmentTypes) => [appointmentType, ...relatedAppointmentTypes],
);

const selectAllAppointmentTypeIds = createSelector(selectAllAppointmentTypes, (appointmentTypes) =>
  appointmentTypes.map((appointmentType) => appointmentType.id),
);

const selectMinimalMaxAdvance = createSelector(
  selectAllAppointmentTypes,
  (appointmentTypes) => appointmentTypes.map((appt) => appt.maxAdvance).sort((a, b) => a - b)[0],
);

const selectContactPhone = createSelector(
  selectAppointmentType,
  (appointmentType) => appointmentType.beauty.bookingOption.contactPhone,
);

const selectAddress = createSelector(selectAppointmentDraft, (draft) =>
  draft.addressDescription && draft.addressDescription.id
    ? draft.addressDescription
    : draft.address,
);

const selectDraftAddress = createSelector(selectAppointmentDraft, (draft) => draft.address);

const selectAppointmentDateSlot = createSelector(
  selectAppointmentDraft,
  ({ dateSlot } = {}) => dateSlot,
);

const selectAppointmentTimeSlot = createSelector(
  selectAppointmentDraft,
  ({ timeSlot } = {}) => timeSlot,
);

const selectSelectedTimeSlotById = (appointmentTypeId) =>
  createSelector(
    selectAppointmentDraftById(appointmentTypeId),
    (appointmentDraft) => appointmentDraft && appointmentDraft.timeSlot,
  );

const selectAppointmentTimeSlotStartTime = createSelector(
  selectAppointmentTimeSlot,
  ({ from } = {}) => from,
);

const selectAppointmentEventId = createSelector(
  selectAppointmentTimeSlot,
  ({ eventId } = {}) => eventId,
);

const selectAppointmentPartner = createSelector(
  selectAppointmentDraft,
  ({ partner } = {}) => partner,
);

const selectPartnerId = createSelector(selectAppointmentPartner, (partner) => partner.id);

const selectPartnersGroups = createSelector(selectAppointmentType, ({ groups } = {}) => groups);

const selectPartnersGroupId = createSelector(
  selectAppointmentDraft,
  (appointmentDraft) => appointmentDraft.partnersGroupId,
);

const isEligibleForMultipleBooking = (feature) =>
  createSelector(
    selectAppointmentType,
    (appointmentType) =>
      feature.enabled &&
      appointmentType &&
      appointmentType.beauty &&
      !!appointmentType.beauty.relatedTreatmentIds.length,
  );

const isMultipleAppointment = createSelector(
  selectRelatedAppointmentDrafts,
  (relatedAppointmentTypes) =>
    relatedAppointmentTypes.length === 1 && !!relatedAppointmentTypes[0].appointmentType.id,
);

const selectProducts = createSelector(selectAppointmentDraft, ({ products }) => products || []);

export const appointmentDraftSelectors = {
  selectAppointmentDraft,
  selectRelatedAppointmentDraftById,
  selectShop,
  selectPartnerId,
  selectAddress,
  selectDraftAddress,
  selectContactPhone,
  selectAppointmentType,
  selectRelatedAppointmentDrafts,
  selectRelatedAppointmentTypeById,
  selectRelatedAppointmentTypesIds,
  selectRelatedAppointmentTypeName,
  selectAppointmentTypeId,
  selectAppointmentTypePrice,
  selectAppointmentTypeName,
  selectAppointmentDateSlot,
  selectAppointmentTimeSlot,
  selectSelectedTimeSlotById,
  selectAppointmentPartner,
  selectPartnersGroups,
  selectPartnersGroupId,
  selectAppointmentTimeSlotStartTime,
  isEligibleForMultipleBooking,
  isMultipleAppointment,
  selectProducts,
  selectAppointmentEventId,
  selectAppointmentDraftById,
  selectMinimalMaxAdvance,
  selectAllAppointmentTypes,
  selectAllAppointmentTypeIds,
  selectRelatedAppointmentTypes,
};
